import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import styles from './JumpGame.module.scss';

// 이미지 import
import frame0 from '../../assets/images/pixil-frame-0.png';
import frame1 from '../../assets/images/pixil-frame-1.png';
import frame2 from '../../assets/images/pixil-frame-2.png';
import frame3 from '../../assets/images/pixil-frame-3.png';
import frame4 from '../../assets/images/pixil-frame-4.png';
import frame5 from '../../assets/images/pixil-frame-5.png';
import playerLoseImg from '../../assets/images/jumpplayerlose.png'; // 게임 오버 이미지 추가
import jump1Easy from '../../assets/images/jump1-1.png';
import jump1Normal from '../../assets/images/jump1-2.png';
import jump1Hard from '../../assets/images/jump1-3.png';
import jump1Expert from '../../assets/images/jump1-4.png';
import jump1Master from '../../assets/images/jump1-5.png';
import impactImg from '../../assets/images/impact.png';
import goldenCoin from '../../assets/images/goldenCoin.png';

// 사운드 import
import flapSound from '../../assets/sounds/flap.wav';
import coinSound from '../../assets/sounds/coin.wav';
import crashSound from '../../assets/sounds/crash.wav';
import successSound from '../../assets/sounds/success.wav';

const GAME_HEIGHT = 800;  // 게임 영역 높이 상수 추가
const GAME_WIDTH = 450;   // 게임 영역 너비 상수 추가

const DIFFICULTY_SETTINGS = {
  EASY: {
    obstacleInterval: 3000,    // 3초마다 장애물 생성
    obstacleSpeed: 3,          // 장애물 이동 속도
    gap: 300,                  // 장애물 사이 간격
    backgroundSpeed: 2,         // 배경 이동 속도
    backgroundImage: jump1Easy,
    targetScore: 10
  },
  NORMAL: {
    obstacleInterval: 2500,
    obstacleSpeed: 3,
    gap: 250,
    backgroundSpeed: 2,
    backgroundImage: jump1Normal,
    targetScore: 10
  },
  HARD: {
    obstacleInterval: 2000,
    obstacleSpeed: 4,
    gap: 220,
    backgroundSpeed: 2,
    backgroundImage: jump1Hard,
    targetScore: 15
  },
  EXPERT: {
    obstacleInterval: 1500,
    obstacleSpeed: 5,
    gap: 200,
    backgroundSpeed: 2.5,
    backgroundImage: jump1Expert,
    targetScore: 15
  },
  MASTER: {
    obstacleInterval: 1200,
    obstacleSpeed: 6,
    gap: 180,
    backgroundSpeed: 3,
    backgroundImage: jump1Master,
    targetScore: 20
  }
};

const JumpGame = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [difficulty, setDifficulty] = useState('EASY'); // 난이도 상태 추가
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [isGameOver, setIsGameOver] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [playerAngle, setPlayerAngle] = useState(0);
  const gameRef = useRef(null);
  const playerRef = useRef(null);
  const obstaclesRef = useRef([]);
  const bgRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastObstacleTimeRef = useRef(0);
  const obstacleGapRef = useRef(3000); // 3초마다 장애물 생성
  const velocityRef = useRef(0);
  const playerYRef = useRef(300);
  const [playerY, setPlayerY] = useState(300);
  const GRAVITY = 0.2;
  const JUMP_FORCE = -5;
  const TERMINAL_VELOCITY = 10;

  // 플레이어 애니메이션 프레임 상태 추가
  const [currentFrame, setCurrentFrame] = useState(0);
  const frames = [frame0, frame1, frame2, frame3, frame4, frame5];
  const animationTimerRef = useRef(null);

  // 충돌 효과 관련 state 추가
  const [impact, setImpact] = useState({ show: false, x: 0, y: 0 });

  const [coins, setCoins] = useState([]);
  const lastCoinTimeRef = useRef(0);
  const COIN_INTERVAL = 5000; // 5초마다 코인 생성
  const MAX_COINS = 5; // 최대 코인 수 제한

  const [audioEnabled, setAudioEnabled] = useState(true);
  const audioContextRef = useRef(null);
  const audioBuffersRef = useRef({});
  
  // 오디오 컨텍스트와 버퍼 초기화
  useEffect(() => {
    // 오디오 컨텍스트 생성
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    
    // 사운드 파일 로드
    const loadAudio = async (url, key) => {
      try {
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
        audioBuffersRef.current[key] = audioBuffer;
      } catch (error) {
        console.error(`Error loading audio ${key}:`, error);
      }
    };

    // 모든 사운드 파일 로드
    loadAudio(flapSound, 'flap');
    loadAudio(coinSound, 'coin');
    loadAudio(crashSound, 'crash');
    loadAudio(successSound, 'success');

    // 사용자 상호작용 후 오디오 컨텍스트 시작
    const startAudioContext = () => {
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      window.removeEventListener('click', startAudioContext);
      window.removeEventListener('keydown', startAudioContext);
    };

    window.addEventListener('click', startAudioContext);
    window.addEventListener('keydown', startAudioContext);

    return () => {
      window.removeEventListener('click', startAudioContext);
      window.removeEventListener('keydown', startAudioContext);
    };
  }, []);

  const playSound = (soundKey) => {
    if (audioEnabled && audioContextRef.current && audioBuffersRef.current[soundKey]) {
      try {
        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBuffersRef.current[soundKey];
        
        const gainNode = audioContextRef.current.createGain();
        gainNode.gain.value = 0.5; // 볼륨 설정
        
        source.connect(gainNode);
        gainNode.connect(audioContextRef.current.destination);
        
        source.start(0);
      } catch (error) {
        console.error(`Error playing sound ${soundKey}:`, error);
      }
    }
  };

  const startGame = useCallback(() => {
    setIsPlaying(true);
    setScore(0);
    setIsGameOver(false);
    setShowSuccessModal(false);
    setPlayerAngle(0);
    velocityRef.current = 0;
    playerYRef.current = 300;
    setPlayerY(300);
    obstaclesRef.current = [];
    setCoins([]); // 코인 초기화
    lastObstacleTimeRef.current = Date.now();
    lastCoinTimeRef.current = Date.now();
    setCurrentFrame(0);
  }, []);

  const jump = useCallback(() => {
    if (isPlaying && !isGameOver) {
      velocityRef.current = JUMP_FORCE;
      setPlayerAngle(-30);
      playSound('flap');
      
      // 애니메이션 시작
      startFrameAnimation();
      
      setTimeout(() => {
        if (!isGameOver) {
          setPlayerAngle(30);
        }
      }, 300);
    }
  }, [isPlaying, isGameOver]);

  // 프레임 애니메이션 시작 함수
  const startFrameAnimation = () => {
    // 이전 타이머 정리
    if (animationTimerRef.current) {
      clearInterval(animationTimerRef.current);
    }
    
    // 프레임 초기화
    setCurrentFrame(1); // 첫 번째 애니메이션 프레임으로 시작
    
    let frameIndex = 1;
    const frameInterval = 100; // 각 프레임 간 간격 (ms)
    
    // 프레임 애니메이션 시작
    animationTimerRef.current = setInterval(() => {
      frameIndex++;
      
      if (frameIndex <= 5) {
        setCurrentFrame(frameIndex);
      } else {
        // 애니메이션 완료 후 기본 프레임으로 돌아감
        setCurrentFrame(0);
        clearInterval(animationTimerRef.current);
        animationTimerRef.current = null;
      }
    }, frameInterval);
  };

  const handleKeyPress = useCallback((event) => {
    if (event.code === 'Space') {
      event.preventDefault();
      // 게임 오버 상태나 성공 모달이 표시된 상태에서는 아무 동작도 하지 않음
      if (isGameOver || showSuccessModal) {
        return;
      }
      
      if (!isPlaying) {
        startGame();
      } else {
        jump();
      }
    }
  }, [isGameOver, isPlaying, startGame, jump, showSuccessModal]);

  const createObstacle = () => {
    const settings = DIFFICULTY_SETTINGS[difficulty];
    const gap = settings.gap;
    const minHeight = 100;
    const maxHeight = GAME_HEIGHT - gap - minHeight;
    const height = Math.random() * (maxHeight - minHeight) + minHeight;

    return {
      top: {
        height: height,
        passed: false
      },
      bottom: {
        height: GAME_HEIGHT - height - gap,
        y: height + gap
      },
      x: GAME_WIDTH
    };
  };

  const moveBackground = () => {
    if (bgRef.current && !isGameOver) {
      const bgPos = parseFloat(bgRef.current.style.backgroundPositionX || '0');
      bgRef.current.style.backgroundPositionX = `${bgPos - 1}px`;
    }
  };

  const createCoin = () => {
    // 현재 코인 수가 최대치에 도달했는지 확인
    if (coins.length >= MAX_COINS) {
      return null;
    }

    const minY = 100;
    const maxY = GAME_HEIGHT - 100;
    const y = Math.random() * (maxY - minY) + minY;
    
    // 장애물과의 최소 거리
    const MIN_DISTANCE_FROM_OBSTACLE = 100; // 거리를 줄임
    
    // 현재 장애물들과의 거리 체크
    const isTooCloseToObstacle = obstaclesRef.current.some(obstacle => {
      // 장애물의 x 좌표가 화면 안에 있을 때만 체크
      if (obstacle.x > -60 && obstacle.x < GAME_WIDTH + 60) {
        // 상단 장애물의 범위
        const topObstacleRange = {
          top: 0,
          bottom: obstacle.top.height,
          left: obstacle.x,
          right: obstacle.x + 60
        };
        
        // 하단 장애물의 범위
        const bottomObstacleRange = {
          top: obstacle.bottom.y,
          bottom: GAME_HEIGHT,
          left: obstacle.x,
          right: obstacle.x + 60
        };
        
        // 코인의 범위
        const coinRange = {
          top: y - 15,
          bottom: y + 15,
          left: GAME_WIDTH - 15,
          right: GAME_WIDTH + 15
        };
        
        // 상단 장애물과의 충돌 체크
        const collisionWithTop = !(
          coinRange.bottom < topObstacleRange.bottom - MIN_DISTANCE_FROM_OBSTACLE ||
          coinRange.top > topObstacleRange.bottom + MIN_DISTANCE_FROM_OBSTACLE ||
          coinRange.right < topObstacleRange.left - MIN_DISTANCE_FROM_OBSTACLE ||
          coinRange.left > topObstacleRange.right + MIN_DISTANCE_FROM_OBSTACLE
        );
        
        // 하단 장애물과의 충돌 체크
        const collisionWithBottom = !(
          coinRange.bottom < bottomObstacleRange.top - MIN_DISTANCE_FROM_OBSTACLE ||
          coinRange.top > bottomObstacleRange.top + MIN_DISTANCE_FROM_OBSTACLE ||
          coinRange.right < bottomObstacleRange.left - MIN_DISTANCE_FROM_OBSTACLE ||
          coinRange.left > bottomObstacleRange.right + MIN_DISTANCE_FROM_OBSTACLE
        );
        
        return collisionWithTop || collisionWithBottom;
      }
      return false;
    });

    // 장애물과 너무 가까우면 null 반환 (코인 생성하지 않음)
    if (isTooCloseToObstacle) {
      return null;
    }
    
    return {
      x: GAME_WIDTH,
      y: y,
      collected: false
    };
  };

  const collectCoin = (coinIndex) => {
    setCoins(prevCoins => {
      const newCoins = [...prevCoins];
      if (!newCoins[coinIndex].collected) {
        newCoins[coinIndex].collected = true;
        setScore(prev => prev + 1);
        playSound('coin');
      }
      return newCoins;
    });
  };

  const gameLoop = () => {
    if (!isPlaying || isGameOver) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    // 플레이어 물리 업데이트
    velocityRef.current += GRAVITY;
    playerYRef.current += velocityRef.current;

    // 바닥과 천장 충돌 체크
    if (playerYRef.current <= 0) {
      playerYRef.current = 0;
      velocityRef.current = 0;
    }
    if (playerYRef.current >= GAME_HEIGHT - 40) {
      gameOver();
      playerYRef.current = GAME_HEIGHT - 40;
      // 게임 오버 후 애니메이션 중단을 위해 여기서 리턴
      return;
    }

    setPlayerY(playerYRef.current);

    const settings = DIFFICULTY_SETTINGS[difficulty];
    
    // 장애물 생성 및 이동
    const now = Date.now();
    if (now - lastObstacleTimeRef.current > settings.obstacleInterval) {
      obstaclesRef.current.push(createObstacle());
      lastObstacleTimeRef.current = now;
    }

    // 장애물 이동 속도 적용
    obstaclesRef.current = obstaclesRef.current.map(obstacle => ({
      ...obstacle,
      x: obstacle.x - settings.obstacleSpeed
    })).filter(obstacle => {
      // 점수 계산
      if (!obstacle.top.passed && obstacle.x < 50) {
        obstacle.top.passed = true;
        setScore(prev => {
          const newScore = prev + 1;
          checkClear(newScore);
          return newScore;
        });
      }

      // 충돌 체크
      const playerHitbox = {
        left: 50,
        right: 90,
        top: playerYRef.current,
        bottom: playerYRef.current + 40
      };

      const topPipe = {
        left: obstacle.x,
        right: obstacle.x + 60,
        top: 0,
        bottom: obstacle.top.height
      };

      const bottomPipe = {
        left: obstacle.x,
        right: obstacle.x + 60,
        top: obstacle.bottom.y,
        bottom: GAME_HEIGHT
      };

      if ((playerHitbox.right > topPipe.left && playerHitbox.left < topPipe.right &&
           playerHitbox.bottom > topPipe.top && playerHitbox.top < topPipe.bottom) ||
          (playerHitbox.right > bottomPipe.left && playerHitbox.left < bottomPipe.right &&
           playerHitbox.bottom > bottomPipe.top && playerHitbox.top < bottomPipe.bottom)) {
        // 충돌 위치 계산 - 약간 더 아래쪽으로 조정
        const impactX = playerHitbox.right;
        const impactY = playerYRef.current + 20; // 20픽셀 아래로 조정
        setImpact({ show: true, x: impactX, y: impactY });
        
        // 0.5초 후에 충돌 효과 제거
        setTimeout(() => {
          setImpact({ show: false, x: 0, y: 0 });
        }, 500);
        
        gameOver();
        return true;
      }

      return obstacle.x > -60;
    });

    // 코인 생성 및 이동
    if (now - lastCoinTimeRef.current > COIN_INTERVAL) {
      const newCoin = createCoin();
      if (newCoin) {
        setCoins(prev => [...prev, newCoin]);
        lastCoinTimeRef.current = now;
      } else {
        // 코인 생성에 실패한 경우, 다음 프레임에서 다시 시도
        lastCoinTimeRef.current = now - COIN_INTERVAL + 1000;
      }
    }

    // 코인 이동 및 충돌 체크
    setCoins(prevCoins => 
      prevCoins.map((coin, index) => {
        if (coin.collected) return coin;
        
        const newX = coin.x - settings.obstacleSpeed;
        
        // 플레이어와 코인 충돌 체크
        const playerHitbox = {
          left: 50,
          right: 90,
          top: playerYRef.current,
          bottom: playerYRef.current + 40
        };

        const coinHitbox = {
          left: newX,
          right: newX + 30,
          top: coin.y,
          bottom: coin.y + 30
        };

        if (playerHitbox.right > coinHitbox.left && 
            playerHitbox.left < coinHitbox.right && 
            playerHitbox.bottom > coinHitbox.top && 
            playerHitbox.top < coinHitbox.bottom) {
          collectCoin(index);
        }

        return { ...coin, x: newX };
      }).filter(coin => coin.x > -30)
    );

    // 배경 이동 속도 적용
    if (bgRef.current && !isGameOver) {
      const bgPos = parseFloat(bgRef.current.style.backgroundPositionX || '0');
      bgRef.current.style.backgroundPositionX = `${bgPos - settings.backgroundSpeed}px`;
    }

    // 게임이 여전히 진행 중이면 다음 프레임 요청
    if (!isGameOver && isPlaying) {
      animationFrameRef.current = requestAnimationFrame(gameLoop);
    }
  };

  const gameOver = () => {
    setIsPlaying(false);
    setIsGameOver(true);
    playSound('crash');
    
    // 애니메이션 프레임 취소
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // 프레임 애니메이션 타이머 정리
    if (animationTimerRef.current) {
      clearInterval(animationTimerRef.current);
      animationTimerRef.current = null;
    }
  };

  const checkClear = (score) => {
    const clearScores = {
      EASY: 10,
      NORMAL: 10,
      HARD: 15,
      EXPERT: 15,
      MASTER: 20
    };
    
    if (score >= clearScores[difficulty]) {
      setIsPlaying(false);
      setShowSuccessModal(true);
      playSound('success');
    }
  };

  const handleNextLevel = () => {
    const difficulties = ['EASY', 'NORMAL', 'HARD', 'EXPERT', 'MASTER'];
    const currentIndex = difficulties.indexOf(difficulty);
    if (currentIndex < difficulties.length - 1) {
      setDifficulty(difficulties[currentIndex + 1]);
    }
    setShowSuccessModal(false);
    startGame();
  };

  // 배경 이미지 변경을 위한 useEffect 추가
  useEffect(() => {
    if (bgRef.current) {
      bgRef.current.style.backgroundImage = `url(${DIFFICULTY_SETTINGS[difficulty].backgroundImage})`;
    }
  }, [difficulty]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (animationTimerRef.current) {
        clearInterval(animationTimerRef.current);
      }
    };
  }, [handleKeyPress]);

  useEffect(() => {
    if (isPlaying && !isGameOver) { // isGameOver 체크 추가
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      animationFrameRef.current = requestAnimationFrame(gameLoop);
    }
  }, [isPlaying, isGameOver]); // isGameOver 의존성 추가

  const calculateProgress = () => {
    const targetScore = DIFFICULTY_SETTINGS[difficulty].targetScore;
    const progress = Math.min((score / targetScore) * 100, 100);
    return progress;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/')}>
          ← {t('gameList')}
        </button>
        <div className={styles.difficultySelector}>
          <select 
            value={difficulty} 
            onChange={(e) => setDifficulty(e.target.value)}
            disabled={isPlaying}
            className={styles.difficultySelect}
          >
            <option value="EASY">쉬움</option>
            <option value="NORMAL">보통</option>
            <option value="HARD">어려움</option>
            <option value="EXPERT">전문가</option>
            <option value="MASTER">마스터</option>
          </select>
        </div>
        <div className={styles.scoreBoard}>
          <div>Score: {score}</div>
        </div>
        <button 
          className={styles.soundButton}
          onClick={() => setAudioEnabled(!audioEnabled)}
        >
          {audioEnabled ? '🔊' : '🔇'}
        </button>
      </div>

      <div 
        className={styles.gameArea}
        ref={gameRef}
        onClick={() => {
          if (!isGameOver && !showSuccessModal) {
            isPlaying ? jump() : startGame();
          }
        }}
      >
        {isPlaying && (
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${calculateProgress()}%` }}
            >
              <div className={styles.progressText}>
                {score}/{DIFFICULTY_SETTINGS[difficulty].targetScore}
              </div>
            </div>
          </div>
        )}
        <div 
          className={styles.background} 
          ref={bgRef} 
          style={{ backgroundImage: `url(${DIFFICULTY_SETTINGS[difficulty].backgroundImage})` }}
        />
        <div 
          className={`${styles.player} ${isGameOver ? '' : styles[`frame${currentFrame}`]}`}
          ref={playerRef}
          style={{ 
            position: 'absolute',
            left: '50px',
            top: `${playerY}px`,
            transform: `rotate(${playerAngle}deg)`,
            transition: 'transform 0.1s linear',
            zIndex: 10,
            backgroundImage: isGameOver ? `url(${playerLoseImg})` : undefined,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center'
          }}
        />
        {obstaclesRef.current.map((obstacle, index) => (
          <React.Fragment key={index}>
            <div 
              className={styles.obstacle} 
              style={{
                height: obstacle.top.height,
                transform: `translateX(${obstacle.x}px)`,
                top: 0
              }}
            />
            <div 
              className={styles.obstacle}
              style={{
                height: obstacle.bottom.height,
                transform: `translateX(${obstacle.x}px)`,
                top: obstacle.bottom.y
              }}
            />
          </React.Fragment>
        ))}
        
        {/* 충돌 효과 추가 */}
        {impact.show && (
          <div
            className={styles.impact}
            style={{
              left: `${impact.x - 30}px`, // 중앙 정렬을 위해 width의 절반만큼 왼쪽으로 이동
              top: `${impact.y - 30}px`   // 중앙 정렬을 위해 height의 절반만큼 위로 이동
            }}
          />
        )}
        
        {coins.map((coin, index) => (
          !coin.collected && (
            <div
              key={index}
              className={styles.coin}
              style={{
                position: 'absolute',
                left: `${coin.x}px`,
                top: `${coin.y}px`,
                width: '30px',
                height: '30px',
                backgroundImage: `url(${goldenCoin})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat'
              }}
            />
          )
        ))}
        
        {!isPlaying && !isGameOver && (
          <div className={styles.startMessage}>
            {t('games.jump.rules.description')}
          </div>
        )}
        
        {isGameOver && (
          <div 
            className={styles.gameOver} 
            style={{ zIndex: 100 }}
            // 이벤트 버블링 방지
            onClick={(e) => e.stopPropagation()}
          >
            <h2>{t('gameOver')}</h2>
            <p>{t('finalScore')}: {score}</p>
            <button onClick={startGame}>{t('playAgain')}</button>
          </div>
        )}
      </div>
      
      {showSuccessModal && (
        <div 
          className={styles.successModal}
          onClick={(e) => e.stopPropagation()}
        >
          <h2>{t('gameClear')}</h2>
          <p>{t('finalScore')}: {score}</p>
          <div className={styles.buttonContainer}>
            <button onClick={startGame}>{t('playAgain')}</button>
            {difficulty !== 'MASTER' && (
              <button onClick={handleNextLevel}>{t('nextLevel')}</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JumpGame; 